import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, Sparkles, Loader2, Star, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BLANK = { name: '', subject: '', body: '', cta_label: 'Finaliser ma commande', language: 'fr', is_default: false };
const SAMPLE = { prenom: 'Marie', produit: 'Montre connectée', total: '49,90 €' };
const LANGS = [
  { code: 'fr', label: 'Français' }, { code: 'en', label: 'English' }, { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' }, { code: 'it', label: 'Italiano' },
];
const GREET = { fr: 'Bonjour', en: 'Hello', es: 'Hola', nl: 'Hallo', it: 'Ciao' };

const renderVars = (s, boutique) => (s || '')
  .replace(/\{prenom\}|\{prénom\}|\{first_name\}/g, SAMPLE.prenom)
  .replace(/\{produit\}|\{product\}/g, SAMPLE.produit)
  .replace(/\{total\}|\{montant\}/g, SAMPLE.total)
  .replace(/\{boutique\}|\{store\}|\{shop\}/g, boutique || 'Ma Boutique');

export function TemplatesPanel({ storeId, storeName, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // template id or null (new)
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [ai, setAi] = useState({ engine: 'gpt', tone: '', instructions: '', product_name: '' });
  const [generating, setGenerating] = useState(false);

  const load = () => api.get('/recovery/templates', { params: { store_id: storeId } })
    .then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { if (storeId) load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(BLANK); setAiOpen(false); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setForm({ ...BLANK, ...t }); setAiOpen(false); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Donnez un nom au template');
    setSaving(true);
    try {
      if (editing) await api.put(`/recovery/templates/${editing}`, form, { params: { store_id: storeId } });
      else await api.post('/recovery/templates', form, { params: { store_id: storeId } });
      toast.success('Template enregistré');
      setOpen(false); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la sauvegarde');
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le template "${t.name}" ?`)) return;
    try { await api.delete(`/recovery/templates/${t.id}`, { params: { store_id: storeId } }); load(); toast.success('Supprimé'); }
    catch { toast.error('Échec de la suppression'); }
  };

  const [seeding, setSeeding] = useState(false);
  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/recovery/templates/seed', null, { params: { store_id: storeId } });
      toast.success(data.created > 0 ? `${data.created} modèle(s) ajouté(s)` : 'Tous les modèles sont déjà présents');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'ajout");
    } finally { setSeeding(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/recovery/ai/generate', { store_id: storeId, ...ai, language: form.language });
      setForm((f) => ({
        ...f,
        subject: data.subject || f.subject,
        body: data.body || f.body,
        cta_label: data.cta_label || f.cta_label,
      }));
      toast.success('Texte généré ✨ — relisez et ajustez avant de sauvegarder');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la génération IA');
    } finally { setGenerating(false); }
  };

  return (
    <div data-testid="recovery-templates-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">Personnalisez vos emails de relance. Variables : <code className="text-xs bg-zinc-100 px-1 rounded">{'{prenom}'}</code> <code className="text-xs bg-zinc-100 px-1 rounded">{'{produit}'}</code> <code className="text-xs bg-zinc-100 px-1 rounded">{'{total}'}</code> <code className="text-xs bg-zinc-100 px-1 rounded">{'{boutique}'}</code></p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seed} disabled={seeding} data-testid="seed-templates-button">
            {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Modèles prêts à l'emploi
          </Button>
          <Button onClick={openNew} data-testid="new-template-button"><Plus className="h-4 w-4 mr-1.5" /> Nouveau</Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card className="p-10 text-center text-zinc-400" data-testid="templates-empty">
          <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Aucun template. Créez-en un ou laissez l'IA rédiger le premier.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => (
            <Card key={t.id} className="p-4 flex flex-col gap-2" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                  {t.is_default && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}{t.name}
                  <span className="text-[10px] uppercase font-mono bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">{t.language || 'fr'}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="text-sm font-medium text-zinc-700 truncate">{t.subject || <span className="text-zinc-400">Pas de sujet</span>}</div>
              <div className="text-xs text-zinc-500 line-clamp-3 whitespace-pre-line">{t.body}</div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le template' : 'Nouveau template'}</DialogTitle>
            <DialogDescription>Rédigez votre relance et prévisualisez le rendu à droite.</DialogDescription>
          </DialogHeader>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nom interne</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Relance urgente" data-testid="template-name-input" />
              </div>
              <div>
                <Label className="text-xs">Sujet de l'email</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Revenez {prenom}, votre panier vous attend" data-testid="template-subject-input" />
              </div>
              <div>
                <Label className="text-xs">Corps du message</Label>
                <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={'Une ligne par paragraphe.\nUtilisez {prenom}, {produit}, {total}...'} data-testid="template-body-input" />
              </div>
              <div>
                <Label className="text-xs">Texte du bouton</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="template-cta-input" />
              </div>
              <div>
                <Label className="text-xs">Langue de l'email</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="h-9" data-testid="template-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-400 mt-1">Pilote la salutation, le pied de page et la génération IA.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="template-default-switch" />
                <Label className="text-xs">Template par défaut (utilisé pour les relances auto)</Label>
              </div>

              <div className="border-t pt-3">
                <button type="button" onClick={() => setAiOpen((v) => !v)} className="flex items-center gap-1.5 text-sm font-medium text-indigo-600" data-testid="toggle-ai-panel">
                  <Sparkles className="h-4 w-4" /> Assistant IA {aiOpen ? '▲' : '▼'}
                </button>
                {aiOpen && (
                  <div className="mt-3 space-y-2 bg-indigo-50/50 border border-indigo-100 rounded-md p-3" data-testid="ai-panel">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Modèle</Label>
                        <Select value={ai.engine} onValueChange={(v) => setAi({ ...ai, engine: v })}>
                          <SelectTrigger className="h-9" data-testid="ai-engine-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gpt">GPT (OpenAI)</SelectItem>
                            <SelectItem value="claude">Claude (Anthropic)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Ton</Label>
                        <Input value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value })} placeholder="amical, urgent..." className="h-9" data-testid="ai-tone-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Produit (contexte)</Label>
                      <Input value={ai.product_name} onChange={(e) => setAi({ ...ai, product_name: e.target.value })} placeholder="Ex: Montre connectée" className="h-9" data-testid="ai-product-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Instructions</Label>
                      <Input value={ai.instructions} onChange={(e) => setAi({ ...ai, instructions: e.target.value })} placeholder="Ex: proposer de l'aide en cas de souci" className="h-9" data-testid="ai-instructions-input" />
                    </div>
                    <Button onClick={generate} disabled={generating} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="ai-generate-button">
                      {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                      Générer le texte
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Live preview */}
            <div>
              <Label className="text-xs text-zinc-500">Aperçu</Label>
              <div className="mt-1 rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm" data-testid="template-preview">
                <div className="font-semibold text-zinc-900 mb-2 pb-2 border-b border-zinc-200">
                  {renderVars(form.subject, storeName) || <span className="text-zinc-400">Sujet…</span>}
                </div>
                <div className="text-zinc-700 mb-1">{GREET[form.language] || 'Bonjour'} {SAMPLE.prenom},</div>
                <div className="text-zinc-600 whitespace-pre-line leading-relaxed">{renderVars(form.body, storeName) || <span className="text-zinc-400">Votre message apparaîtra ici…</span>}</div>
                <div className="mt-4">
                  <span className="inline-block bg-zinc-900 text-white text-xs font-semibold px-4 py-2 rounded-md">{form.cta_label || 'Finaliser ma commande'}</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-template-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
